import { CTA, ReviewPageConfig, ReviewPageConfigSettings, normalizeCTA } from "@/services/bedrock";

const TEXT_MAX_LENGTH = 255;

type LiquidField = "headline" | "subtitle";
type LiquidFieldErrors = Partial<Record<LiquidField, string>>;

const getReviewPageSettings = (config: ReviewPageConfig): ReviewPageConfigSettings => ({
  formId: config.formId ?? null,
  contentPageThemeId: config.contentPageThemeId ?? null,
  headline: config.headline ?? null,
  subtitle: config.subtitle ?? null,
  showStarSummary: config.showStarSummary,
  showSentimentSummary: config.showSentimentSummary,
  showNpsSummary: config.showNpsSummary,
  useCondensedQuotes: config.useCondensedQuotes,
  approvedForSocialOnly: config.approvedForSocialOnly,
  reviewSortType: config.reviewSortType,
  showReviewReplies: config.showReviewReplies,
  showSourceFilter: config.showSourceFilter,
  showOfficeDetails: config.showOfficeDetails,
  showSocialLinks: config.showSocialLinks,
  callToAction: normalizeCTA(config.callToAction),
});

const callToActionParams = (cta: CTA | null): [string, string][] => {
  if (!cta) return [["callToAction", ""]];
  const { label, type, customLink, formId, smartLinkId } = cta;
  return Object.entries({ label, type, customLink, formId, smartLinkId })
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => [`callToAction[${key}]`, String(value)]);
};

const reviewPagePreviewParams = (settings: Partial<ReviewPageConfigSettings>): URLSearchParams => {
  const { callToAction, ...rest } = settings;
  return new URLSearchParams([
    ...Object.entries(rest).map(([key, value]): [string, string] => [
      key,
      value === null || value === undefined ? "" : String(value),
    ]),
    ...(callToAction === undefined ? [] : callToActionParams(callToAction)),
  ]);
};

const LIQUID_FIELD_LABELS: Record<string, LiquidField> = {
  Headline: "headline",
  Subtitle: "subtitle",
};

const liquidFieldErrors = (message: unknown): LiquidFieldErrors => {
  if (typeof message !== "string") return {};
  const errors: LiquidFieldErrors = {};
  const labels = Object.keys(LIQUID_FIELD_LABELS).join("|");
  const pattern = new RegExp(`(${labels}) (.+?)(?=, (?:${labels}) |$)`, "g");
  for (const [, label, error] of message.replace(/^Validation failed: /, "").matchAll(pattern)) {
    errors[LIQUID_FIELD_LABELS[label]] = `${label} ${error}.`;
  }
  return errors;
};

export { TEXT_MAX_LENGTH, getReviewPageSettings, reviewPagePreviewParams, liquidFieldErrors };
export type { LiquidField, LiquidFieldErrors };
